<script setup>
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useI18n } from 'vue-i18n';

import semver from 'semver';

const { t } = useI18n();
const { currentAccount } = useAccount();

const latestChatwootVersion = computed(() => {
  return currentAccount.value.latest_chatwoot_version;
});

const globalConfig = useMapGetter('globalConfig/get');

const hasAnUpdateAvailable = computed(() => {
  if (!semver.valid(latestChatwootVersion.value)) {
    return false;
  }

  return semver.lt(globalConfig.value.appVersion, latestChatwootVersion.value);
});

const gitSha = computed(() => {
  return globalConfig.value.gitSha.substring(0, 7);
});

const copyGitSha = () => {
  copyTextToClipboard(globalConfig.value.gitSha);
};
</script>

<template>
  <div class="flex flex-col w-full outline outline-1 outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak">
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('GENERAL_SETTINGS.FORM.BUILD_INFO.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.BUILD_INFO.NOTE') }}
      </p>
    </div>

    <div class="px-5 py-4 text-sm">
      <div v-if="hasAnUpdateAvailable && globalConfig.displayManifest" class="mb-3">
        {{
          t('GENERAL_SETTINGS.UPDATE_CHATWOOT', {
            latestChatwootVersion: latestChatwootVersion,
          })
        }}
      </div>

      <div class="grid gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-11">
            {{ t('GENERAL_SETTINGS.FORM.BUILD_INFO.VERSION') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ `v${globalConfig.appVersion}` }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-11">
            {{ t('GENERAL_SETTINGS.FORM.BUILD_INFO.BUILD') }}
          </span>
          <button
            v-tooltip="t('COMPONENTS.CODE.BUTTON_TEXT')"
            type="button"
            class="build-id rounded-md bg-n-alpha-2 px-2 py-1 font-medium text-n-slate-12 transition hover:bg-n-alpha-3"
            @click="copyGitSha"
          >
            {{ gitSha }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
